import { BASC4ParsedResult } from "@api/assessments/assessment-schema-types";
import type { AssessmentLayoutConfig } from "@components/AssessmentFormRenderer/types/layoutTypes";

const NAME_COLUMN = (label: string) => ({ key: "Name", label, editable: false });

const SCORE_COLUMNS = [
  {
    key: "Raw Score",
    label: "Raw Score",
    editable: true,
    validation: { min: 0, step: 1, inputType: "number" as const },
  },
  {
    key: "T Score",
    label: "T Score",
    editable: true,
    validation: { min: 10, max: 120, step: 1, inputType: "number" as const },
  },
  { key: "Percentile Rank", label: "Percentile Rank", editable: true },
  { key: "Confidence Interval", label: "90% Confidence Interval", editable: true },
];

const SECTIONS: AssessmentLayoutConfig["sections"] = [
  {
    type: "single_select",
    key: "Form",
    label: "Form",
    editable: true,
    options: [
      { label: "Parent Rating Scales (PRS)", value: "PRS" },
      { label: "Teacher Rating Scales (TRS)", value: "TRS" },
      { label: "Self-Report of Personality (SRP)", value: "SRP" },
    ],
  },
  { type: "text_field", key: "Level", label: "Level", editable: true },
  { type: "text_field", key: "Test Date", label: "Test Date", editable: true, validation: { inputType: "date" } },
  { type: "text_field", key: "Rater Name", label: "Rater Name", editable: true },
  { type: "text_field", key: "Relationship", label: "Relationship", editable: true },
  { type: "text_field", key: "Rater Position", label: "Rater Position", editable: true },
  { type: "text_field", key: "Norm Group", label: "Norm Group", editable: true },
  {
    type: "score_table",
    label: "Validity Indexes",
    source: "Validity Indexes",
    columns: [
      NAME_COLUMN("Index"),
      { key: "Raw Score", label: "Raw Score", editable: true, validation: { min: 0, step: 1, inputType: "number" } },
      { key: "Classification", label: "Classification", editable: true },
    ],
  },
  {
    type: "score_table",
    label: "Composites",
    source: "Composites",
    columns: [NAME_COLUMN("Composite"), ...SCORE_COLUMNS],
  },
  {
    type: "score_table",
    label: "Primary Scales",
    source: "Primary Scales",
    columns: [NAME_COLUMN("Scale"), ...SCORE_COLUMNS],
  },
  {
    type: "score_table",
    label: "Content Scales",
    source: "Content Scales",
    columns: [NAME_COLUMN("Scale"), ...SCORE_COLUMNS],
  },
  {
    type: "score_table",
    label: "Clinical Indexes",
    source: "Clinical Indexes",
    columns: [NAME_COLUMN("Index"), ...SCORE_COLUMNS],
  },
  {
    type: "score_table",
    label: "Emotional Disturbance Qualification (EDQ) Scales",
    source: "EDQ Scales",
    columns: [
      NAME_COLUMN("Scale"),
      ...SCORE_COLUMNS,
      { key: "Classification", label: "Classification", editable: true },
    ],
  },
  {
    type: "text_field",
    key: "Social Maladjustment Indicator",
    label: "Social Maladjustment Indicator",
    editable: true,
  },
];

// Whole-number cells are the score_table columns that declare step 1.
const WHOLE_NUMBER_CELLS = SECTIONS.flatMap((section) =>
  section.type === "score_table"
    ? section.columns
        .filter((column) => column.validation?.step === 1)
        .map((column) => ({ list: section.source, key: column.key }))
    : [],
);

// FDM stores these scores as integers and drops a save that has a fraction,
// so the form rejects one on the cell instead. The generated schema has no
// integer check.
const BASC_4_FORM_SCHEMA = BASC4ParsedResult.superRefine((value, ctx) => {
  for (const { list, key } of WHOLE_NUMBER_CELLS) {
    const rows = (value as Record<string, unknown>)[list];
    (Array.isArray(rows) ? rows : []).forEach((row, index) => {
      const score = (row as Record<string, unknown>)[key];
      if (typeof score === "number" && !Number.isInteger(score)) {
        ctx.addIssue({ code: "custom", path: [list, index, key], message: "Enter a whole number" });
      }
    });
  }
});

export const BASC_4_LAYOUT: AssessmentLayoutConfig = {
  parsedResultSchema: BASC_4_FORM_SCHEMA,
  sections: SECTIONS,
};
